/**
 * New Sporttikone asset for sections/gallery.liquid + snippets/gallery-lightbox-modal.liquid
 * (not an upstream Enterprise file - see CUSTOMIZATIONS.md "Gallery section (lightbox)").
 *
 * Opens the theme's own product-page lightbox (assets/gallery-zoom.js's
 * <gallery-zoom> - unmodified here, pinch-zoom/pan/thumbnails/keyboard nav
 * all vendor code) at the clicked block's slide. Standing in for
 * <gallery-zoom-open> (assets/gallery-zoom.js) and <media-gallery>
 * (assets/media-gallery.js), which this section can't use directly - both
 * are hardcoded to the product page (<media-gallery> requires a `.js-product`
 * ancestor it throws without; <gallery-zoom-open> looks up its modal via
 * `this.closest('media-gallery').galleryModal`, a clone-to-body cache
 * `media-gallery.js` builds). This does the same two calls their handlers
 * make - modal.open(), then tell <gallery-zoom> which slide - without either
 * dependency, since snippets/gallery-lightbox-modal.liquid renders the
 * modal-dialog directly on the page rather than cloning it from a template.
 */

if (!customElements.get('gallery-lightbox-open')) {
  class GalleryLightboxOpen extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.handleClick.bind(this));
    }

    /**
     * Open the referenced modal's <gallery-zoom> at this block's slide.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const modal = document.getElementById(this.dataset.modal);
      const zoom = modal?.querySelector('gallery-zoom');
      if (!modal || !zoom) return;

      modal.open(this);
      zoom.init(this.dataset.mediaId, modal);
      zoom.focus();
      evt.preventDefault();
    }
  }

  window.customElements.define('gallery-lightbox-open', GalleryLightboxOpen);
}

/**
 * <gallery-zoom-caption> - shows the active slide's caption (a Gallery
 * block's Heading, snippets/gallery-lightbox-modal.liquid's `data-caption`
 * on each .gallery-zoom__thumb) underneath the lightbox media. Doesn't
 * reimplement any part of the zoom/thumbnail logic: GalleryZoom.selectThumb()
 * (assets/gallery-zoom.js, untouched) already does two things on every
 * slide change - swaps .gallery-zoom__zoom-container's child image, and
 * toggles .gallery-zoom__thumb--active onto the matching thumb - this only
 * watches for the first to know when to re-read the second.
 */
if (!customElements.get('gallery-zoom-caption')) {
  class GalleryZoomCaption extends HTMLElement {
    connectedCallback() {
      const zoom = this.closest('gallery-zoom');
      const zoomContainer = zoom?.querySelector('.gallery-zoom__zoom-container');
      if (!zoom || !zoomContainer) return;

      this.zoom = zoom;
      this.updateCaption = this.updateCaption.bind(this);
      new MutationObserver(this.updateCaption).observe(zoomContainer, { childList: true });
      this.updateCaption();
    }

    /**
     * Copy the active thumbnail's data-caption across, hiding this element
     * when there isn't one.
     */
    updateCaption() {
      const activeThumb = this.zoom.querySelector('.gallery-zoom__thumb--active');
      const caption = activeThumb?.dataset.caption || '';
      this.textContent = caption;
      this.hidden = caption === '';
    }
  }

  window.customElements.define('gallery-zoom-caption', GalleryZoomCaption);
}
